import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Grid,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { AddOn, SelectedAddOn } from '@acorn-portal/shared/src/services/renewalsApi';
import parser from 'html-react-parser';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import { getAdditionalExtraText } from '../../utils/get-additional-extra-text';

interface IProps {
	onSelect: (item: SelectedAddOn[]) => void;
	onRemove: (id: string) => void;
	option: AddOn[];
}

const TaxiHire: FC<IProps> = ({ onSelect, onRemove, option }) => {
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const renewalStore = useAppSelector((redux) => redux.renewal);
	const Hire14Day = option?.find((i) => i.addOnCode === 'CAR_HIRE_14');
	const Hire21Day = option?.find((i) => i.addOnCode === 'CAR_HIRE_21');
	const paymentInFull = renewalStore.paymentOptions.method === 'total';
	const [selectedOption, setSelectedOption] = useState<SelectedAddOn>(
		option.find((x) => x.selected === true) ?? option[0],
	);
	const currentSelectedOption = option.find(
		(o) => o.underwrittenDataPointId === selectedOption.underwrittenDataPointId,
	)?.selected;

	if (!Hire14Day && !Hire21Day) {
		return null;
	}

	const handleChange = (_: React.MouseEvent<HTMLElement>, value: string) => {
		const opt = option.find((o) => o.addOnCode === value);
		if (!opt) return;
		setSelectedOption(opt);
	};

	const getCheapestOption = () => {
		if (Hire14Day && Hire21Day) {
			if (paymentInFull) {
				return Hire21Day.annualCost < Hire14Day.annualCost ? Hire21Day.annualCost : Hire14Day.annualCost;
			}
			return Hire21Day.monthlyCost < Hire14Day.monthlyCost ? Hire21Day.monthlyCost : Hire14Day.monthlyCost;
		}
		return 0;
	};

	const onAdd = (item: SelectedAddOn) => {
		let newSelectedAddOns = [
			...renewalStore.addons,
			{
				underwrittenDataPointId: item.underwrittenDataPointId,
				addOnCode: item.addOnCode,
			},
		];

		if (item.addOnCode === 'CAR_HIRE_14') {
			newSelectedAddOns = newSelectedAddOns.filter((addon) => addon.addOnCode.indexOf('CAR_HIRE_21') === -1);
		}
		if (item.addOnCode === 'CAR_HIRE_21') {
			newSelectedAddOns = newSelectedAddOns.filter((addon) => addon.addOnCode.indexOf('CAR_HIRE_14') === -1);
		}
		onSelect(newSelectedAddOns);
	};

	const renderToggleButtons = () =>
		option.map((o) => {
			const dayAmt = o.addOnCode.split('_').slice(-1);
			return (
				<ToggleButton
					key={o.addOnCode}
					fullWidth
					value={o.addOnCode}
					sx={(theme) => ({
						'&.Mui-selected, &.Mui-selected:hover': {
							color: theme.palette.common.white,
							backgroundColor: theme.palette.primary.main,
						},
					})}
				>
					{dayAmt} Day
				</ToggleButton>
			);
		});

	const getPricingHeader = (paymentInFull: boolean, selectedOption: { addOnCode: string }) => {
		const Hire14DaySelected = selectedOption?.addOnCode === 'CAR_HIRE_14';
		const Hire21DaySelected = selectedOption?.addOnCode === 'CAR_HIRE_21';

		if (!Hire14DaySelected && !Hire21DaySelected) {
			return (
				<Typography variant='h5' fontWeight='bold'>
					{formatCurrency(getCheapestOption())}
					{!paymentInFull && ' per month'}
				</Typography>
			);
		}

		if (!paymentInFull) {
			if (Hire14DaySelected && Hire14Day?.monthlyCost) {
				return (
					<Typography variant='h5' fontWeight='bold'>
						{formatCurrency(Hire14Day?.monthlyCost)} per month
					</Typography>
				);
			}
			if (Hire21DaySelected && Hire21Day?.monthlyCost) {
				return (
					<Typography variant='h5' fontWeight='bold'>
						{formatCurrency(Hire21Day?.monthlyCost)} per month
					</Typography>
				);
			}
		}
		if (paymentInFull) {
			if (Hire14DaySelected && Hire14Day?.annualCost) {
				return (
					<Typography variant='h5' fontWeight='bold'>
						{formatCurrency(Hire14Day?.annualCost)}
					</Typography>
				);
			}

			if (Hire21DaySelected && Hire21Day?.annualCost) {
				return (
					<Typography variant='h5' fontWeight='bold'>
						{formatCurrency(Hire21Day?.annualCost)}
					</Typography>
				);
			}

			return null;
		}
	};

	// Extracted dayLabel logic from nested ternary
	let dayLabel = 'from';
	if (Hire14Day && Hire21Day && (Hire14Day.selected || Hire21Day.selected)) {
		if (Hire14Day.selected) {
			dayLabel = ' - 14 Day';
		} else {
			dayLabel = ' - 21 Day';
		}
	}

	return (
		<Box
			sx={(theme) => ({
				border: `2px solid ${theme.palette.grey[200]}`,
				borderRadius: 2,
				backgroundColor: '#fff',
				'& .MuiOutlinedInput-root': {
					backgroundColor: theme.palette.background.paper,
				},
			})}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					my: 1,
				}}
			>
				<Box>
					<Typography sx={{ pl: 2, pt: 2, fontWeight: 'bold' }}>
						Taxi Hire Cover {dayLabel}
						{getPricingHeader(paymentInFull, selectedOption)}
					</Typography>
				</Box>
				{(Hire14Day?.selected || Hire21Day?.selected) && (
					<Box sx={{ pt: 2, pr: 2 }}>
						<CheckCircleIcon color='primary' fontSize='large' />
					</Box>
				)}
			</Box>

			{renewalStore.paymentOptions.method && Hire14Day && Hire21Day && (
				<Grid container spacing={2} sx={{ pl: 2, pr: 2 }}>
					<ToggleButtonGroup
						disabled={currentSelectedOption}
						value={selectedOption.addOnCode}
						exclusive
						onChange={handleChange}
						aria-label='text alignment'
						fullWidth
						sx={{ height: '50px', pl: 2, pt: 1.2 }}
					>
						{renderToggleButtons()}
					</ToggleButtonGroup>
					{Hire14Day.selected || Hire21Day.selected ? (
						<Grid size={{ xs: 12, md: 12 }}>
							<Button
								fullWidth
								variant='contained'
								startIcon={<DeleteIcon />}
								onClick={() => {
									onRemove(
										Hire14Day?.selected ? Hire14Day?.underwrittenDataPointId : Hire21Day?.underwrittenDataPointId,
									);
								}}
							>
								Remove
							</Button>
						</Grid>
					) : (
						<Grid size={{ xs: 12, md: 12 }}>
							<Button
								disabled={!selectedOption}
								fullWidth
								variant='contained'
								startIcon={<AddIcon />}
								onClick={() => {
									onAdd(selectedOption);
								}}
							>
								Add
							</Button>
						</Grid>
					)}
				</Grid>
			)}

			<Accordion
				sx={(theme) => ({
					backgroundColor: 'unset',
					borderRadius: 'unset',
					borderTopLeftRadius: 'unset',
					borderTopRightRadius: 'unset',
					boxShadow: 'unset',
					borderBottom: `1px solid ${theme.palette.grey[300]}`,
					m: 1,

					'&:before': {
						backgroundColor: 'unset',
					},
					'&:last-of-type': {
						borderBottomLeftRadius: 'unset',
						borderBottomRightRadius: 'unset',
					},
				})}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='tell-me-more-content' id='tell-me-more-header'>
					<Typography>Tell me more</Typography>
				</AccordionSummary>
				<AccordionDetails>
					{parser(getAdditionalExtraText('Taxi Replacement Hire', fullPolicy.scheme, renewalStore.renewalStartDate))}
				</AccordionDetails>
			</Accordion>
		</Box>
	);
};

export default TaxiHire;
